import gql from 'graphql-tag';
import Vue from 'vue';
import { DollarApollo } from 'vue-apollo/types/vue-apollo';
import { SimpleSuccessResponse } from '@/interfaces/basic/SimpleSuccessResponse';
import { LegalDocCode } from '@/enums/user/LegalDocCode';
import { AcceptLegalDocs } from '@/queries/user/AcceptLegalDocs';

const activateAccountMutation = gql`
  mutation($code: String!) {
    activateAccount(code: $code)
  }
`;

const acceptInvitationMutation = gql`
  mutation($code: String!) {
    acceptInvitation(code: $code)
  }
`;

const requestPasswordRestoringMutation = gql`
  mutation($email: String!) {
    requestPasswordRestoring(email: $email) {
      success
      error
    }
  }
`;

const resetPasswordMutation = gql`
  mutation($code: String!, $newPassword: String!) {
    resetPassword(code: $code, newPassword: $newPassword) {
      success
      error
    }
  }
`;

export async function activateAccount(
  apollo: DollarApollo<any>,
  code: string,
) {
  try {
    await apollo.mutate({
      mutation: activateAccountMutation,
      variables: {
        code,
      },
    });
  } catch (e) {
    throw e;
  }
}

export async function acceptInvitation(
  apollo: DollarApollo<any>,
  code: string,
) {
  try {
    return await apollo.mutate({
      mutation: acceptInvitationMutation,
      variables: {
        code,
      },
    });
  } catch (e) {
    throw e;
  }
}

export async function requestPasswordRestoring(
  apollo: DollarApollo<any>,
  email: string,
) {
  try {
    const result = await apollo.mutate({
      mutation: requestPasswordRestoringMutation,
      variables: {
        email,
      },
    });
    if (result.data == undefined) {
      throw new Error('Cannot get request password restoring result');
    }
    const response: SimpleSuccessResponse = result.data.requestPasswordRestoring;
    if (!response.success) {
      throw new Error(response.error);
    }
  } catch (e) {
    if (e instanceof Error && e.message.match('GraphQL error')) {
      throw new Error(Vue.i18n.translate('General_Server_Error'));
    }
    throw e;
  }
}

export async function resetPassword(
  apollo: DollarApollo<any>,
  code: string,
  newPassword: string,
) {
  try {
    const result = await apollo.mutate({
      mutation: resetPasswordMutation,
      variables: {
        code,
        newPassword,
      },
    });
    if (result.data == undefined) {
      throw new Error('Cannot get password resetting result');
    }
    const response: SimpleSuccessResponse = result.data.resetPassword;
    if (!response.success) {
      throw new Error(response.error);
    }
  } catch (e) {
    throw e;
  }
}

export async function acceptLegalDocs(
  apollo: DollarApollo<any>,
  codes: LegalDocCode[],
) {
  await apollo.mutate<boolean, AcceptLegalDocs.VariablesDefinition>({
    mutation: AcceptLegalDocs.mutation,
    variables: {
      codes,
    },
    awaitRefetchQueries: true,
    refetchQueries: ['meRefetchable'],
  });
}
